import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, Package, Truck, Home, MapPin } from "lucide-react";
import api, { formatMoney } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

const statusMap = {
  pending: { label: "Pending", color: "bg-slate-100 text-slate-700" },
  awaiting_payment: { label: "Awaiting Payment", color: "bg-amber-100 text-amber-800" },
  paid: { label: "Paid", color: "bg-emerald-100 text-emerald-800" },
  processing: { label: "Processing", color: "bg-blue-100 text-blue-800" },
  shipped: { label: "Shipped", color: "bg-purple-100 text-purple-800" },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800" },
};

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${orderId}`).then((res) => setOrder(res.data)).catch(() => setOrder(false));
  }, [orderId]);

  if (order === false) return <div className="max-w-3xl mx-auto p-16 text-center">Order not found.</div>;
  if (!order)
    return (
      <div className="max-w-3xl mx-auto p-8 space-y-4">
        <Skeleton className="h-12 w-3/4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );

  const s = statusMap[order.status] || statusMap.pending;

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
          <Check className="w-9 h-9 text-emerald-600" />
        </div>
        <h1 className="mt-5 text-3xl font-black text-slate-900">Thank you for your order!</h1>
        <p className="mt-1 text-slate-600">Order <span className="font-bold text-slate-900">{order.order_number}</span></p>
        <Badge className={`mt-3 ${s.color}`}>{s.label}</Badge>
      </div>

      <div className="mt-8 bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="font-bold text-lg text-slate-900 flex items-center gap-2"><Package className="w-5 h-5" /> Items</h2>
        <div className="mt-4 divide-y divide-slate-100">
          {order.items.map((it) => (
            <div key={it.product_id} className="py-3 flex gap-3">
              <div className="w-16 h-16 rounded-md bg-slate-50 border overflow-hidden shrink-0">
                {it.image && <img src={it.image} alt="" className="w-full h-full object-contain" />}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-slate-900">{it.name}</div>
                <div className="text-xs text-slate-500">SKU {it.sku} • Qty {it.quantity}</div>
              </div>
              <div className="font-bold text-[#0b3d75]">{formatMoney(it.price * it.quantity)}</div>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-200 mt-3 pt-3 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span>{formatMoney(order.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-slate-600">Tax</span><span>{formatMoney(order.tax)}</span></div>
          <div className="flex justify-between"><span className="text-slate-600">Shipping</span><span>{order.shipping_cost === 0 ? "FREE" : formatMoney(order.shipping_cost)}</span></div>
          <div className="flex justify-between text-lg font-black pt-2 border-t border-slate-200"><span>Total</span><span className="text-[#0b3d75]">{formatMoney(order.total)}</span></div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-lg p-6">
        <h2 className="font-bold text-lg text-slate-900 flex items-center gap-2"><MapPin className="w-5 h-5" /> Shipping To</h2>
        <div className="mt-3 text-sm text-slate-700">
          <div className="font-bold">{order.shipping.full_name}</div>
          <div>{order.shipping.address_line1}{order.shipping.address_line2 ? `, ${order.shipping.address_line2}` : ""}</div>
          <div>{order.shipping.city}, {order.shipping.state} {order.shipping.zip_code}</div>
          <div>{order.shipping.country}</div>
          <div className="text-slate-500 mt-1">{order.shipping.email} • {order.shipping.phone}</div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <Link to="/shop" className="text-[#0b3d75] font-semibold hover:text-amber-600 inline-flex items-center gap-1">
          <Home className="w-4 h-4" /> Back to Shop
        </Link>
      </div>
    </div>
  );
};

export default OrderConfirmation;
